import { useEffect, useState } from "react";

import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";

import L from "leaflet";

import "leaflet/dist/leaflet.css";

import { FaLocationArrow, FaSearch, FaCheck } from "react-icons/fa";

import { IoClose } from "react-icons/io5";

import styles from "./Locationpicker.module.css";

/* =========================================================
   FIX LEAFLET DEFAULT MARKER
========================================================= */

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",

  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",

  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

/* =========================================================
   MAP CENTER
========================================================= */

const MapCenter = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (!position) return;

    map.flyTo([position.lat, position.lng], 17, {
      animate: true,
      duration: 0.8,
    });
  }, [position, map]);

  return null;
};

/* =========================================================
   MAP CLICK HANDLER
========================================================= */

const MapClickHandler = ({ onLocationChange }) => {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });

  return null;
};

/* =========================================================
   LOCATION PICKER
========================================================= */

const LocationPicker = ({ onClose, onConfirm, initialLocation = null }) => {
  /* =======================================================
     DEFAULT LOCATION
  ======================================================= */

  const defaultPosition = {
    lat: 17.385044,
    lng: 78.486671,
  };

  /* =======================================================
     STATES
  ======================================================= */

  const [position, setPosition] = useState(
    initialLocation?.latitude && initialLocation?.longitude
      ? {
          lat: Number(initialLocation.latitude),
          lng: Number(initialLocation.longitude),
        }
      : null,
  );

  const [address, setAddress] = useState(initialLocation?.address || "");

  const [addressDetails, setAddressDetails] = useState(initialLocation || null);

  const [searchText, setSearchText] = useState("");

  const [loadingLocation, setLoadingLocation] = useState(false);

  const [loadingAddress, setLoadingAddress] = useState(false);

  const [error, setError] = useState("");

  /* =======================================================
     REVERSE GEOCODING
  ======================================================= */

  const getAddressFromCoordinates = async (lat, lng) => {
    try {
      setLoadingAddress(true);

      setError("");

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Unable to get address");
      }

      const result = await response.json();

      const data = result?.address || {};

      const city =
        data.city ||
        data.town ||
        data.village ||
        data.municipality ||
        data.county ||
        "";

      const state = data.state || "";

      const zip = data.postcode || "";

      const detectedAddress = result?.display_name || "Selected location";

      const locationData = {
        latitude: lat,

        longitude: lng,

        address: detectedAddress,

        city,

        state,

        zip,

        country: data.country || "India",

        country_code: data.country_code || "in",

        rawAddress: data,
      };

      setAddress(detectedAddress);

      setAddressDetails(locationData);

      setLoadingAddress(false);

      return locationData;
    } catch (err) {
      console.error("Reverse geocoding error:", err);

      setLoadingAddress(false);

      const fallbackData = {
        latitude: lat,

        longitude: lng,

        address: "",

        city: "",

        state: "",

        zip: "",

        country: "India",

        country_code: "in",

        rawAddress: {},
      };

      setAddress("Location selected. Please enter your address details.");

      setAddressDetails(fallbackData);

      return fallbackData;
    }
  };

  /* =======================================================
     SELECT POSITION
  ======================================================= */

  const selectPosition = async (lat, lng) => {
    setPosition({
      lat,
      lng,
    });

    await getAddressFromCoordinates(lat, lng);
  };

  /* =======================================================
     CURRENT LOCATION
  ======================================================= */

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Your browser does not support location.");

      return;
    }

    setLoadingLocation(true);

    setError("");

    navigator.geolocation.getCurrentPosition(
      async (location) => {
        const lat = location.coords.latitude;

        const lng = location.coords.longitude;

        await selectPosition(lat, lng);

        setLoadingLocation(false);
      },

      (locationError) => {
        console.error("Location error:", locationError);

        setLoadingLocation(false);

        if (locationError.code === 1) {
          setError("Location permission denied. Please allow location access.");
        } else if (locationError.code === 2) {
          setError("Your location could not be determined.");
        } else if (locationError.code === 3) {
          setError("Location request timed out. Please try again.");
        } else {
          setError("Unable to get your current location.");
        }
      },

      {
        enableHighAccuracy: true,

        timeout: 15000,

        maximumAge: 0,
      },
    );
  };

  /* =======================================================
     SEARCH LOCATION
  ======================================================= */

  /* =======================================================
   SEARCH LOCATION
======================================================= */

  /* =======================================================
   SEARCH LOCATION
======================================================= */

const handleSearch = async () => {
  const originalQuery = searchText.trim();

  if (!originalQuery) {
    setError("Please enter a location.");
    return;
  }

  try {
    setLoadingAddress(true);
    setError("");

    /*
     * =====================================================
     * CLEAN SEARCH QUERY
     * =====================================================
     */

    // Remove "near Hyderabad" because we will explicitly
    // add Hyderabad below.
    const cleanedQuery = originalQuery
      .replace(/\bnear\s+hyderabad\b/gi, "")
      .replace(/\bhyderabad\b/gi, "")
      .replace(/\s+/g, " ")
      .trim();

    /*
     * =====================================================
     * IMPORTANT:
     * ALWAYS SEARCH HYDERABAD FIRST
     * =====================================================
     */

    const queries = [
      `${cleanedQuery}, Hyderabad, Telangana, India`,
      `${cleanedQuery}, Hyderabad, India`,
      `${cleanedQuery}, Telangana, India`,
      `${originalQuery}, Hyderabad, Telangana, India`,
    ];

    console.log("Search queries:", queries);

    /*
     * =====================================================
     * HYDERABAD BOUNDING BOX
     *
     * This prevents results such as Thailand.
     *
     * Hyderabad approximate bounds:
     * South: 17.20
     * North: 17.60
     * West: 78.20
     * East: 78.70
     * =====================================================
     */

    const HYDERABAD_BOUNDS = {
      minLat: 17.20,
      maxLat: 17.60,
      minLng: 78.20,
      maxLng: 78.70,
    };

    const isHyderabadLocation = (lat, lng) => {
      return (
        lat >= HYDERABAD_BOUNDS.minLat &&
        lat <= HYDERABAD_BOUNDS.maxLat &&
        lng >= HYDERABAD_BOUNDS.minLng &&
        lng <= HYDERABAD_BOUNDS.maxLng
      );
    };

    /*
     * =====================================================
     * TRY PHOTON
     * =====================================================
     */

    for (const query of queries) {
      try {
        const photonUrl =
          `https://photon.komoot.io/api/` +
          `?q=${encodeURIComponent(query)}` +
          `&limit=10` +
          `&lat=${defaultPosition.lat}` +
          `&lon=${defaultPosition.lng}`;

        console.log("Photon search:", query);

        const response = await fetch(photonUrl);

        if (!response.ok) {
          continue;
        }

        const data = await response.json();

        console.log("Photon response:", data);

        /*
         * =================================================
         * FILTER ONLY HYDERABAD RESULTS
         * =================================================
         */

        const validResults = (data?.features || []).filter((feature) => {
          const coordinates = feature?.geometry?.coordinates;

          if (!coordinates || coordinates.length < 2) {
            return false;
          }

          const lng = Number(coordinates[0]);
          const lat = Number(coordinates[1]);

          return (
            !Number.isNaN(lat) &&
            !Number.isNaN(lng) &&
            isHyderabadLocation(lat, lng)
          );
        });

        console.log("Valid Hyderabad results:", validResults);

        if (validResults.length > 0) {
          const foundResult = validResults[0];

          const coordinates =
            foundResult.geometry.coordinates;

          const lng = Number(coordinates[0]);
          const lat = Number(coordinates[1]);

          console.log(
            "FOUND HYDERABAD LOCATION:",
            foundResult
          );

          setPosition({
            lat,
            lng,
          });

          await getAddressFromCoordinates(lat, lng);

          setLoadingAddress(false);

          return;
        }
      } catch (photonError) {
        console.warn("Photon error:", photonError);
      }
    }

    /*
     * =====================================================
     * FALLBACK TO NOMINATIM
     * =====================================================
     */

    for (const query of queries) {
      try {
        /*
         * viewbox restricts the search around Hyderabad.
         *
         * left  = 78.20
         * top   = 17.60
         * right = 78.70
         * bottom= 17.20
         */

        const nominatimUrl =
          `https://nominatim.openstreetmap.org/search` +
          `?format=jsonv2` +
          `&q=${encodeURIComponent(query)}` +
          `&limit=10` +
          `&addressdetails=1` +
          `&dedupe=1` +
          `&countrycodes=in` +
          `&viewbox=78.20,17.60,78.70,17.20` +
          `&bounded=1`;

        console.log("Nominatim search:", query);

        const response = await fetch(nominatimUrl, {
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          continue;
        }

        const results = await response.json();

        console.log("Nominatim results:", results);

        /*
         * =================================================
         * FILTER ONLY HYDERABAD RESULTS
         * =================================================
         */

        const validResults = results.filter((result) => {
          const lat = Number(result.lat);
          const lng = Number(result.lon);

          return (
            !Number.isNaN(lat) &&
            !Number.isNaN(lng) &&
            isHyderabadLocation(lat, lng)
          );
        });

        console.log(
          "Valid Hyderabad Nominatim results:",
          validResults
        );

        if (validResults.length > 0) {
          const result = validResults[0];

          const lat = Number(result.lat);
          const lng = Number(result.lon);

          console.log(
            "FOUND HYDERABAD NOMINATIM LOCATION:",
            result
          );

          setPosition({
            lat,
            lng,
          });

          await getAddressFromCoordinates(lat, lng);

          setLoadingAddress(false);

          return;
        }
      } catch (nominatimError) {
        console.warn(
          "Nominatim error:",
          nominatimError
        );
      }
    }

    /*
     * =====================================================
     * NOTHING FOUND IN HYDERABAD
     * =====================================================
     */

    setLoadingAddress(false);

    setError(
      `"${originalQuery}" could not be found in Hyderabad. Try searching with the area name, for example "My Friend Circle Restaurant, Madhapur".`
    );
  } catch (error) {
    console.error("Search error:", error);

    setLoadingAddress(false);

    setError(
      "Unable to search location. Please try again."
    );
  }
};

  /* =======================================================
     MAP LOCATION
  ======================================================= */

  const handleMapLocation = async (lat, lng) => {
    await selectPosition(lat, lng);
  };

  /* =======================================================
     CONFIRM LOCATION
  ======================================================= */

  const handleConfirm = () => {
    if (!position) {
      setError("Please select your delivery location.");

      return;
    }

    if (loadingAddress) {
      return;
    }

    const data = {
      ...(addressDetails || {}),

      latitude: position.lat,

      longitude: position.lng,

      address: addressDetails?.address || address || "",

      city: addressDetails?.city || "",

      state: addressDetails?.state || "Telangana",

      zip: addressDetails?.zip || "",

      country: addressDetails?.country || "India",

      country_code: addressDetails?.country_code || "in",
    };

    console.log("CONFIRMED LOCATION:", data);

    onConfirm(data);
  };

  /* =======================================================
     MAP POSITION
  ======================================================= */

  const mapPosition = position
    ? [position.lat, position.lng]
    : [defaultPosition.lat, defaultPosition.lng];

  return (
    <div className={styles.overlay}>
      <div className={styles.locationModal}>
        {/* =================================================
            HEADER
        ================================================= */}

        <div className={styles.header}>
          <div>
            <h2>Location Information</h2>

            <span className={styles.headerSubtitle}>
              Select where you want your order delivered
            </span>
          </div>

          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <IoClose size={22} />
          </button>
        </div>

        {/* =================================================
            SEARCH
        ================================================= */}

        <div className={styles.searchWrapper}>
          <FaSearch className={styles.searchIcon} />

          <input
            type="text"
            placeholder="Search for area, street, landmark..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />

          <button
            type="button"
            className={styles.searchBtn}
            onClick={handleSearch}
            disabled={loadingAddress}
          >
            {loadingAddress ? "..." : "Search"}
          </button>
        </div>

        {/* =================================================
            MAP
        ================================================= */}

        <div className={styles.mapWrapper}>
          <MapContainer
            center={mapPosition}
            zoom={position ? 17 : 12}
            scrollWheelZoom={true}
            className={styles.map}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {position && <Marker position={[position.lat, position.lng]} />}

            <MapCenter position={position} />

            <MapClickHandler onLocationChange={handleMapLocation} />
          </MapContainer>

          {/* =================================================
              MAP MESSAGE
          ================================================= */}

          <div className={styles.locationMessage}>
            <strong>Order will be delivered here</strong>

            <span>Move the map pin to your exact location</span>
          </div>

          {/* =================================================
              CURRENT LOCATION
          ================================================= */}

          <button
            type="button"
            className={styles.currentLocationBtn}
            onClick={handleCurrentLocation}
            disabled={loadingLocation}
          >
            <FaLocationArrow />

            {loadingLocation ? "Detecting..." : "Use Current Location"}
          </button>
        </div>

        {/* =================================================
            SELECTED LOCATION
        ================================================= */}

        <div className={styles.bottomSection}>
          <div className={styles.bottomTitleRow}>
            <h3>Select a delivery location</h3>

            {position && !loadingAddress && (
              <span className={styles.selectedBadge}>
                <FaCheck />
                Selected
              </span>
            )}
          </div>

          {loadingAddress ? (
            <div className={styles.loadingBox}>
              <span className={styles.spinner} />

              <span>Detecting your address...</span>
            </div>
          ) : address ? (
            <div className={styles.selectedAddress}>
              <span className={styles.locationDot} />

              <div className={styles.addressContent}>
                <strong>Selected Location</strong>

                <p>{address}</p>
              </div>
            </div>
          ) : (
            <p className={styles.helperText}>
              Search for an address, click on the map, or use your current
              location.
            </p>
          )}

          {error && <p className={styles.errorText}>{error}</p>}

          {/* =================================================
              ACTION BUTTONS
          ================================================= */}

          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.searchLocationBtn}
              onClick={() => {
                document
                  .querySelector(`.${styles.searchWrapper} input`)
                  ?.focus();
              }}
            >
              <FaSearch />
              Search Location
            </button>

            <button
              type="button"
              className={styles.confirmBtn}
              onClick={handleConfirm}
              disabled={!position || loadingAddress || loadingLocation}
            >
              <FaCheck />
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
